/**
 * SatelliteOrbit Component
 * Renders a dotted line orbit path around Earth to simulate satellite trajectories
 */

import { useMemo, useRef, useEffect, useLayoutEffect } from 'react'
import { BufferGeometry, Vector3, LineDashedMaterial } from 'three'
import { useFrame } from '@react-three/fiber'

export function SatelliteOrbit({
  radius = 1.5,
  segments = 128,
  rotationX = 0,
  rotationY = 0,
  rotationZ = 0,
  color = '#E3E3E3',
  opacity = 1,
  dashSize = 0.025,
  gapSize = 0.075,
  rotationSpeed = 0.05
}) {
  const lineRef = useRef()
  const groupRef = useRef()
  
  // Generate orbit circle geometry
  const geometry = useMemo(() => {
    const points = []
    
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2
      const x = Math.cos(angle) * radius
      const z = Math.sin(angle) * radius
      const y = 0
      
      points.push(new Vector3(x, y, z))
    }
    
    const geo = new BufferGeometry().setFromPoints(points)
    return geo
  }, [radius, segments])

  // Create material once
  const material = useMemo(() => {
    return new LineDashedMaterial({
      color: color,
      opacity: opacity,
      transparent: true,
      dashSize: dashSize,
      gapSize: gapSize,
      depthWrite: false,
      depthTest: true
    })
  }, [color, opacity, dashSize, gapSize])

  // Compute line distances BEFORE first paint
  useLayoutEffect(() => {
    if (lineRef.current) {
      lineRef.current.computeLineDistances()
    }
  }, [geometry])

  // Cleanup geometry and material on unmount
  useEffect(() => {
    return () => {
      geometry.dispose()
      material.dispose()
    }
  }, [geometry, material])

  // Rotate orbit at same speed as Earth
  useFrame((state, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += rotationSpeed * delta
    }
  })

  return (
    <group ref={groupRef} rotation={[rotationX, 0, rotationZ]}>
      <line ref={lineRef} geometry={geometry} material={material} />
    </group>
  )
}
